import { z } from 'zod'
import { isNil } from 'lodash'
import { Decimal } from 'decimal.js'
import type { Translator } from '~/types/i18n'

// Default max decimals for settlement calculations - matches nuxt.config.ts runtimeConfig
export const MAX_DECIMALS = 3

/**
 * Allow us to set translated default messages, to avoid having to override each time on the schema
 * @param t
 */
export function configureZodErrorMap(t: Translator) {
  const customZodErrorsMessage = (issue: z.core.$ZodRawIssue) => {
    switch (issue.code) {
      case 'invalid_type':
        if (issue.message === 'Required')
          return t('validation.required')

        return t('validation.invalid')

      case 'too_small':
        if (issue.type === 'string') {
          if (issue.minimum === 1)
            return t('validation.required')

          return t('validation.string.too-small', { minimum: issue.minimum })
        }
        else if (issue.type === 'number') {
          return t('validation.number.too-small', { minimum: issue.minimum })
        }
        break

      case 'too_big':
        if (issue.type === 'string') {
          if (issue.maximum === 0)
            return t('validation.blank')

          return t('validation.string.too-big', { maximum: issue.maximum })
        }
        else if (issue.type === 'number') {
          return t('validation.number.too-big', { maximum: issue.maximum })
        }
        break
    }
  }
  z.config({
    customError: customZodErrorsMessage,
  })
}

export function zodRequiredString(error = 'Required') {
  return z.string({ error }).min(1, { error })
}
export function zodNull(error: string) {
  return z.null({
    error,
  })
}

export function zodEmptyString(error: string) {
  return z.string({ error }).max(0, { error })
}

export function zodGetFieldValue(fields: { name: string, value: string }[], fieldName: string) {
  return fields.find(f => f.name === fieldName)?.value || ''
}

export function zodDecimal(error?: string, precision: number = MAX_DECIMALS) {
  return z
    .union([zodRequiredString(), z.number()])
    .transform((val) => {
      if (isNil(val))
        return val
      try {
        return new Decimal(val).toDecimalPlaces(precision).toNumber()
      }
      catch (_error) {
        return val
      }
    })
    .refine(val => typeof val === 'number' && !Number.isNaN(val), {
      error,
    })
}
